import React, { Fragment, useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { ArrowBack, KeyboardArrowRight } from '@clds/icon';
import Tooltip from '@clds/tooltip';
import { asStyledComponent } from '@cld/component-enhancer';
import CrumbButton from './CrumbButton';
import { getHiddenCrumbComponent, isStringOrNumber } from './helpers';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 16px;

  * {
    user-select: none;
  }

  ${CrumbButton}:first-child {
    flex-shrink: 0;
    flex-basis: auto;
  }

  ${CrumbButton}:last-child {
    flex-shrink: 1;
    flex-basis: auto;
    font-weight: bold;
  }
`;

const DividerWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
`;

const DefaultDivider = () => <KeyboardArrowRight color="secondaryAlt" size="sm" />;

const Breadcrumbs = ({ children, onCrumbClick, maxCrumbs = 5, isMobile, Divider = DefaultDivider, ...props }) => {
  const crumbsToDisplay = useMemo(() => {
    const childrenArray = React.Children.toArray(children);
    const getOnClick = (index) => () => onCrumbClick(index);
    const list = childrenArray.map((child, index) => {
      const tooltipTitle = isStringOrNumber(child) ? child : '';
      return { children: child, tooltipTitle, onClick: getOnClick(index), key: index };
    });

    if (!isMobile) {
      if (list.length > maxCrumbs) {
        const hiddenCount = list.length - maxCrumbs;
        const hiddenCrumbs = getHiddenCrumbComponent(list, hiddenCount);
        list.splice(1, hiddenCount, hiddenCrumbs);
      }
    } else if (list.length - 1 > 0) {
      const leafIndex = list.length - 1;
      list.splice(0, leafIndex, {
        children: <ArrowBack size="md" />,
        onClick: getOnClick(leafIndex - 1),
        key: leafIndex - 1,
        className: 'back-arrow',
      });
    }

    return list;
  }, [children, isMobile, onCrumbClick, maxCrumbs]);

  return (
    <Wrapper data-test="breadcrumbs" {...props}>
      {crumbsToDisplay.map(({ disabled, tooltipTitle, forceTooltip, children, ...restItem }, index) => {
        const isRootElement = index === 0;

        const disableBreadcrumb = index === crumbsToDisplay.length - 1 || !!disabled;

        return (
          <Fragment key={index}>
            {!isRootElement && !isMobile && (
              <DividerWrapper>
                <Divider />
              </DividerWrapper>
            )}
            <Tooltip title={tooltipTitle}>
              <CrumbButton as={disableBreadcrumb ? 'span' : undefined} disabled={disableBreadcrumb} {...restItem} data-test="crumb-button">
                {forceTooltip ? <span>{children}</span> : <Tooltip.OverflowTrigger>{children}</Tooltip.OverflowTrigger>}
              </CrumbButton>
            </Tooltip>
          </Fragment>
        );
      })}
    </Wrapper>
  );
};

Breadcrumbs.propTypes = {
  onCrumbClick: PropTypes.func,
  maxCrumbs: PropTypes.number,
  isMobile: PropTypes.bool,
  Divider: PropTypes.oneOfType([PropTypes.node, PropTypes.elementType]),
};

export default asStyledComponent(Breadcrumbs);
